import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  ORGANIZATION_ID_PATH_PARAM,
  Product,
  PredefinedForm,
  InventoryItem,
  UserRole,
  Organization,
  UserDepartment,
  UserInOrganization,
} from '@equip-track/shared';
import { OrganizationStore } from '../store/organization.store';
import { UserStore } from '../store/user.store';

export interface SelectedOrganizationInfo {
  organizationId: string;
  organization: Organization;
  selectedAt: number; // timestamp for potential cache invalidation
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  // Use a different key to avoid conflicts with UserStore
  private readonly apiService = inject(ApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly organizationStore = inject(OrganizationStore);
  private readonly userStore = inject(UserStore);

  async getUsers(): Promise<void> {
    this.organizationStore.setGetUsersLoading(true);

    try {
      const orgId = this.userStore.selectedOrganizationId();
      if (!orgId) {
        const errorMessage = this.translateService.instant(
          'organization.users.get.error'
        );
        this.organizationStore.setGetUsersError(errorMessage);
        return;
      }
      const getUsersResponse = await firstValueFrom(
        this.apiService.endpoints.getUsers.execute(undefined, {
          [ORGANIZATION_ID_PATH_PARAM]: orgId,
        })
      );
      if (getUsersResponse.status) {
        this.organizationStore.setUsers(getUsersResponse.users);
        this.organizationStore.setGetUsersSuccess();
      } else {
        const error =
          getUsersResponse.errorMessage ||
          this.translateService.instant('organization.users.get.error');
        this.notificationService.showError('errors.users.fetch-failed', error);
        this.organizationStore.setGetUsersError(error);
      }
    } catch (error: unknown) {
      console.error('Error getting users', error);
      this.notificationService.handleApiError(
        error,
        'errors.users.fetch-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.users.get.error'
      );
      this.organizationStore.setGetUsersError(errorMessage);
    }
  }

  async fetchProducts(): Promise<void> {
    const organizationId = this.userStore.selectedOrganizationId();
    if (!organizationId) {
      throw new Error('No organization selected');
    }

    try {
      this.organizationStore.setGetProductsLoading();
      const result = await firstValueFrom(
        this.apiService.endpoints.getProducts.execute(undefined, {
          [ORGANIZATION_ID_PATH_PARAM]: organizationId,
        })
      );
      if (result.status) {
        this.organizationStore.setProducts(result.products);
        this.organizationStore.setGetProductsSuccess();
      } else {
        const errorMessage =
          result.errorMessage ||
          this.translateService.instant('organization.products.get.error');
        console.error(
          'Error response from fetching products:',
          result.errorMessage
        );
        this.notificationService.showError(
          'errors.products.fetch-failed',
          errorMessage
        );
        this.organizationStore.setGetProductsError(errorMessage);
      }
    } catch (error: unknown) {
      console.error('Error fetching products', error);
      this.notificationService.handleApiError(
        error,
        'errors.products.fetch-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.products.get.error'
      );
      this.organizationStore.setGetProductsError(errorMessage);
    }
  }

  async fetchPredefinedForms(): Promise<void> {
    const organizationId = this.userStore.selectedOrganizationId();
    if (!organizationId) {
      return;
    }

    try {
      this.organizationStore.setGetPredefinedFormsLoading();
      const result = await firstValueFrom(
        this.apiService.endpoints.getPredefinedForms.execute(undefined, {
          [ORGANIZATION_ID_PATH_PARAM]: organizationId,
        })
      );
      if (result.status) {
        const forms = result.forms.map((form) =>
          this.sanitizePredefinedForm(form, organizationId)
        );
        this.organizationStore.setPredefinedForms(forms);
        this.organizationStore.setGetPredefinedFormsSuccess();
      } else {
        const errorMessage =
          result.errorMessage ||
          this.translateService.instant('organization.predefined-kits.get.error');
        console.error(
          'Error response from fetching predefined forms:',
          result.errorMessage
        );
        this.notificationService.showError(
          'errors.predefined-kits.fetch-failed',
          errorMessage
        );
        this.organizationStore.setGetPredefinedFormsError(errorMessage);
      }
    } catch (error: unknown) {
      console.error('Error fetching predefined forms', error);
      this.notificationService.handleApiError(
        error,
        'errors.predefined-kits.fetch-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.predefined-kits.get.error'
      );
      this.organizationStore.setGetPredefinedFormsError(errorMessage);
    }
  }

  async savePredefinedForm(form: PredefinedForm): Promise<boolean> {
    this.organizationStore.setSavePredefinedFormLoading(true);

    try {
      const organizationId = this.userStore.selectedOrganizationId();
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const sanitized = this.sanitizePredefinedForm(form, organizationId);
      const result = await firstValueFrom(
        this.apiService.endpoints.addPredefinedForm.execute(
          { form: sanitized },
          { [ORGANIZATION_ID_PATH_PARAM]: organizationId }
        )
      );

      if (result.status) {
        this.organizationStore.upsertPredefinedForm(sanitized);
        this.notificationService.showSuccess(
          'organization.predefined-kits.save-success'
        );
        this.organizationStore.setSavePredefinedFormSuccess();
        return true;
      }

      const errorMessage =
        result.errorMessage ||
        this.translateService.instant('organization.predefined-kits.save.error');
      this.notificationService.showError(
        'errors.predefined-kits.save-failed',
        errorMessage
      );
      this.organizationStore.setSavePredefinedFormError(errorMessage);
      return false;
    } catch (error: unknown) {
      console.error('Error saving predefined form', error);
      this.notificationService.handleApiError(
        error,
        'errors.predefined-kits.save-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.predefined-kits.save.error'
      );
      this.organizationStore.setSavePredefinedFormError(errorMessage);
      return false;
    }
  }

  async deletePredefinedForm(formID: string): Promise<boolean> {
    this.organizationStore.setDeletePredefinedFormLoading(true);

    try {
      const organizationId = this.userStore.selectedOrganizationId();
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const result = await firstValueFrom(
        this.apiService.endpoints.deletePredefinedForm.execute(
          { formID },
          { [ORGANIZATION_ID_PATH_PARAM]: organizationId }
        )
      );

      if (result.status) {
        this.organizationStore.removePredefinedForm(formID);
        this.notificationService.showSuccess(
          'organization.predefined-kits.delete-success'
        );
        this.organizationStore.setDeletePredefinedFormSuccess();
        return true;
      }

      const errorMessage =
        result.errorMessage ||
        this.translateService.instant('organization.predefined-kits.delete.error');
      this.notificationService.showError(
        'errors.predefined-kits.delete-failed',
        errorMessage
      );
      this.organizationStore.setDeletePredefinedFormError(errorMessage);
      return false;
    } catch (error: unknown) {
      console.error('Error deleting predefined form', error);
      this.notificationService.handleApiError(
        error,
        'errors.predefined-kits.delete-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.predefined-kits.delete.error'
      );
      this.organizationStore.setDeletePredefinedFormError(errorMessage);
      return false;
    }
  }

  private sanitizePredefinedForm(
    form: PredefinedForm,
    organizationId: string
  ): PredefinedForm {
    return {
      organizationID: organizationId,
      formID: form.formID,
      description: form.description,
      items: (form.items ?? []).map((item) => this.sanitizeInventoryItem(item)),
    };
  }

  private sanitizeInventoryItem(item: InventoryItem): InventoryItem {
    const sanitized: InventoryItem = {
      productId: item.productId,
      source: item.source,
      quantity: item.quantity,
    };
    if (item.upis?.length) {
      sanitized.upis = [...item.upis];
    }
    return sanitized;
  }

  async saveProduct(product: Product): Promise<boolean> {
    // Add loading indicator for save product operation
    this.organizationStore.setSaveProductLoading(true);

    try {
      const organizationId = this.userStore.selectedOrganizationId();
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const result = await firstValueFrom(
        this.apiService.endpoints.setProduct.execute(
          { product },
          { [ORGANIZATION_ID_PATH_PARAM]: organizationId }
        )
      );

      if (result.status) {
        // Update the product in the store
        const currentProducts = this.organizationStore.products();
        const existingIndex = currentProducts.findIndex(
          (p) => p.id === product.id
        );

        if (existingIndex >= 0) {
          // Update existing product
          const updatedProducts = [...currentProducts];
          updatedProducts[existingIndex] = product;
          this.organizationStore.setProducts(updatedProducts);
        } else {
          // Add new product
          this.organizationStore.setProducts([...currentProducts, product]);
        }

        this.notificationService.showSuccess(
          'organization.products.save-success',
          'Product saved successfully'
        );
        this.organizationStore.setSaveProductSuccess();
        return true;
      } else {
        const errorMessage =
          result.errorMessage ||
          this.translateService.instant('organization.products.save.error');
        console.error('Error saving product:', errorMessage);
        this.notificationService.showError(
          'errors.products.save-failed',
          errorMessage
        );
        this.organizationStore.setSaveProductError(errorMessage);
        return false;
      }
    } catch (error: unknown) {
      console.error('Error saving product:', error);
      this.notificationService.handleApiError(
        error,
        'errors.products.save-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.products.save.error'
      );
      this.organizationStore.setSaveProductError(errorMessage);
      return false;
    }
  }

  async deleteProduct(productId: string): Promise<boolean> {
    // Add loading indicator for delete product operation
    this.organizationStore.setDeleteProductLoading(true);

    try {
      const organizationId = this.userStore.selectedOrganizationId();
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const result = await firstValueFrom(
        this.apiService.endpoints.deleteProduct.execute(
          { productId },
          { [ORGANIZATION_ID_PATH_PARAM]: organizationId }
        )
      );

      if (result.status) {
        // Remove the product from the store
        const currentProducts = this.organizationStore.products();
        const updatedProducts = currentProducts.filter(
          (p) => p.id !== productId
        );
        this.organizationStore.setProducts(updatedProducts);
        this.notificationService.showSuccess(
          'organization.products.delete-success',
          'Product deleted successfully'
        );
        this.organizationStore.setDeleteProductSuccess();
        return true;
      } else {
        const errorMessage =
          result.errorMessage ||
          this.translateService.instant('organization.products.delete.error');
        console.error('Error deleting product:', errorMessage);
        this.notificationService.showError(
          'errors.products.delete-failed',
          errorMessage
        );
        this.organizationStore.setDeleteProductError(errorMessage);
        return false;
      }
    } catch (error: unknown) {
      console.error('Error deleting product:', error);
      this.notificationService.handleApiError(
        error,
        'errors.products.delete-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.products.delete.error'
      );
      this.organizationStore.setDeleteProductError(errorMessage);
      return false;
    }
  }

  async inviteUser(email: string, name: string, role: UserRole, department: UserDepartment): Promise<boolean> {
    this.organizationStore.setInvitingUserLoading(true);

    try {
      const result = await firstValueFrom(
        this.apiService.endpoints.inviteUser.execute(
          {
            email,
            name,
            role,
            organizationId: this.userStore.selectedOrganizationId(),
            department,
          },
          {
            [ORGANIZATION_ID_PATH_PARAM]:
              this.userStore.selectedOrganizationId(),
          }
        )
      );

      if (!result.status) {
        throw result;
      }

      this.organizationStore.setInvitingUserSuccess();
      this.organizationStore.addUser(result);
      this.notificationService.showSuccess(
        'organization.users.invite-success',
        'User invitation sent successfully'
      );
      return true;
    } catch (error: unknown) {
      console.error('Error inviting user', error);
      this.notificationService.handleApiError(
        error,
        'errors.users.invite-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.users.invite.error'
      );
      this.organizationStore.setInvitingUserError(errorMessage);
      return false;
    }
  }

  async updateUser(
    userInOrganization: UserInOrganization
  ): Promise<boolean> {
    this.organizationStore.setSetUserLoading(true);

    try {
      const organizationId = this.userStore.selectedOrganizationId();
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const result = await firstValueFrom(
        this.apiService.endpoints.setUser.execute(
          { userInOrganization },
          { [ORGANIZATION_ID_PATH_PARAM]: organizationId }
        )
      );

      if (result.status) {
        const existing = this.organizationStore.getUser(
          userInOrganization.userId
        );
        if (existing) {
          this.organizationStore.updateUser({
            user: existing.user,
            userInOrganization,
          });
        }
        this.notificationService.showSuccess(
          'organization.users.edit.success'
        );
        this.organizationStore.setSetUserSuccess();
        return true;
      }

      const errorMessage =
        result.errorMessage ||
        this.translateService.instant('organization.users.edit.error');
      this.notificationService.showError(
        'errors.users.update-failed',
        errorMessage
      );
      this.organizationStore.setSetUserError(errorMessage);
      return false;
    } catch (error: unknown) {
      console.error('Error updating user', error);
      this.notificationService.handleApiError(
        error,
        'errors.users.update-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.users.edit.error'
      );
      this.organizationStore.setSetUserError(errorMessage);
      return false;
    }
  }

  async deleteUser(userId: string): Promise<boolean> {
    this.organizationStore.setDeleteUserLoading(true);

    try {
      const organizationId = this.userStore.selectedOrganizationId();
      if (!organizationId) {
        throw new Error('No organization selected');
      }

      const result = await firstValueFrom(
        this.apiService.endpoints.deleteUser.execute(
          { userId },
          { [ORGANIZATION_ID_PATH_PARAM]: organizationId }
        )
      );

      if (result.status) {
        this.organizationStore.removeUser(userId);
        this.notificationService.showSuccess(
          'organization.users.delete.success'
        );
        this.organizationStore.setDeleteUserSuccess();
        return true;
      }

      const errorMessage =
        result.errorMessage ||
        this.translateService.instant('organization.users.delete.error');
      this.notificationService.showError(
        'errors.users.delete-failed',
        errorMessage
      );
      this.organizationStore.setDeleteUserError(errorMessage);
      return false;
    } catch (error: unknown) {
      console.error('Error deleting user', error);
      this.notificationService.handleApiError(
        error,
        'errors.users.delete-failed'
      );
      const errorMessage = this.translateService.instant(
        'organization.users.delete.error'
      );
      this.organizationStore.setDeleteUserError(errorMessage);
      return false;
    }
  }
}
